import { useId, type ReactNode } from 'react';

export type FieldProps = {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
};

/**
 * Wires a label, hint and error message to a control by id.
 *
 * The render-prop shape exists so the control keeps its own type and props
 * while the accessibility wiring lives in exactly one place.
 */
export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="flex flex-col gap-(--space-1)">
      {/* Bold, like the labels above the header controls: a form and a filter
          row should not disagree about what a label looks like. */}
      <label htmlFor={id} className="text-[length:var(--text-small)] font-bold text-ink">
        {label}
      </label>
      {hint !== undefined && (
        <p id={hintId} className="text-[length:var(--text-small)] text-ink-subtle">
          {hint}
        </p>
      )}
      {children({
        id,
        describedBy: describedBy === '' ? undefined : describedBy,
        invalid: error !== undefined,
      })}
      {error !== undefined && (
        <p id={errorId} role="alert" className="text-[length:var(--text-small)] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
